/**
 * O diálogo de confirmação do SPA — o compartilhado pelos módulos (há outros `role="dialog"`, como
 * a folha "Mais" do `Shell` e as fichas modais do cadastro).
 *
 * **Ele nasceu privado dentro de `FichaDoNegocio.tsx` (3.10) e saiu para cá na 4.4**, sem uma
 * linha de comportamento mudada: os testes daquele arquivo — `Esc`, Cancelar, `aria-modal`, o foco
 * que entra — rodam sobre este componente exatamente como rodavam sobre o anterior, e é isso que
 * prova que a extração não mexeu em nada.
 *
 * A razão da mudança é uma direção de import: `FichaDoNegocio` importa `TarefasDoNegocio`, e o
 * cartão de Tarefas passou a precisar do mesmo diálogo para concluir (UX-DR6). Importá-lo de volta
 * da ficha fecharia um ciclo — e uma segunda cópia dentro do cartão divergiria no primeiro ajuste
 * de acessibilidade, que é justamente o que não pode divergir: o foco entra no diálogo, `Esc`
 * fecha, e `aria-modal` diz ao leitor de tela que o resto da página está fora de alcance.
 *
 * **Ele se pinta sozinho, e isso é do arquivo e não do acaso.** As classes que este markup usa —
 * inclusive as dos dois botões e a da recusa — são declaradas em `dialogo.css`, que é o único CSS
 * que ele importa. Enquanto o componente morava dentro da ficha ele usava `.ficha__botao` e
 * `.ficha__erro` de `ficha-do-negocio.css`, e funcionava porque a ficha estava sempre na mesma
 * árvore; a tela de Tarefas (4.6) monta o diálogo sem ela por perto, e ali o acidente cobraria.
 *
 * **A prop `acaoSecundaria` é a única coisa que a 4.4 acrescentou**, e ela substitui o botão
 * "Cancelar" quando presente. O modal de conclusão tem **dois** botões que concluem ("Concluir sem
 * resultado" e "Concluir e sugerir próximo passo", protótipo `:996-1001`), e não um par
 * cancelar/confirmar — sem ela, o cartão precisaria desenhar o próprio rodapé e a extração não
 * teria servido para nada. Ausente, o rodapé continua exatamente como estava.
 */

import { type ReactNode, useCallback, useEffect, useRef } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import './dialogo.css'

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** O que substitui o "Cancelar" do rodapé — rótulo e o que fazer. */
export interface AcaoSecundaria {
  rotulo: string
  aoAcionar: () => void
}

export interface PropriedadesDoDialogo {
  titulo: string
  subtitulo: string
  rotuloDoConfirmar: string
  confirmarDesabilitado?: boolean
  /**
   * Sem o botão de confirmar (13.12): a confirmação que ainda conta, que esbarrou no bloqueio do
   * serviço ou que não conseguiu ler o que confirmaria. Desabilitado ainda ofereceria um "sim" que
   * não existe; ausente, o rodapé só tem a saída.
   */
  semConfirmar?: boolean
  ocupado: boolean
  erro: unknown
  aoFechar: () => void
  aoConfirmar: () => void
  /**
   * O botão da esquerda, quando ele **não** é "Cancelar".
   *
   * Ele fica desabilitado junto do primário enquanto a escrita está no ar, pela mesma razão do
   * Cancelar: os dois saem daqui, e um deles reabilitado no meio do envio seria o segundo clique.
   */
  acaoSecundaria?: AcaoSecundaria
  children?: ReactNode
}

/**
 * Um diálogo de confirmação, no molde da folha "Mais" do `Shell`.
 *
 * Ele é um componente porque os atos que o usam pedem a **mesma** coisa: um título, um subtítulo,
 * um corpo opcional, e o par de botões do rodapé. Cópias divergiriam no primeiro ajuste — e é
 * justamente a acessibilidade que não pode divergir.
 */
export function Dialogo({
  titulo,
  subtitulo,
  rotuloDoConfirmar,
  confirmarDesabilitado = false,
  semConfirmar = false,
  ocupado,
  erro,
  aoFechar,
  aoConfirmar,
  acaoSecundaria,
  children,
}: PropriedadesDoDialogo) {
  const caixa = useRef<HTMLDivElement>(null)

  // O foco entra no diálogo assim que ele existe. `tabIndex={-1}` o torna focável por código sem
  // colocá-lo na ordem de tabulação. É o mesmo desenho da folha "Mais".
  useEffect(() => {
    caixa.current?.focus()
  }, [])

  /*
   * As três saídas do diálogo respeitam `ocupado`, e não só o botão Cancelar.
   *
   * Com a mutação em voo, o Cancelar fica `disabled` — mas `Esc` e o clique no fundo continuavam
   * chamando `aoFechar` por caminhos que o atributo não alcança. Fechar ali desmonta o diálogo com
   * a escrita a caminho: a recusa do serviço não teria onde aparecer, e quem confirmou a perda
   * ficaria sem saber se ela aconteceu. Uma saída só, e ela vale para as três.
   */
  const fechar = useCallback(() => {
    if (ocupado) {
      return
    }
    aoFechar()
  }, [ocupado, aoFechar])

  // `Esc` fecha. É o que se espera de qualquer sobreposição — e, no celular, é a saída que não
  // exige acertar o botão Cancelar com o polegar.
  useEffect(() => {
    /* Em **captura**, e marcando o evento (11.4): este diálogo pode estar empilhado sobre o modal
       do Negócio, e o `Esc` fecha só o de cima — o modal de baixo ignora o evento já marcado. */
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key !== 'Escape' || evento.defaultPrevented) {
        return
      }
      /* 13.5: um diálogo **irmão** aberto por cima deste (a ficha do "+ Empresa" sobre a troca da
         Empresa do Negócio) é quem responde ao `Esc`. "Por cima" é depois na ordem do documento,
         fora desta caixa, **modal** e **visível** — o modal do Negócio, que contém este diálogo, vem
         antes; um diálogo oculto, inerte ou não modal montado depois não rouba o `Esc`. */
      const proprio = caixa.current
      if (
        proprio !== null &&
        [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')].some(
          (outro) =>
            outro !== proprio &&
            !proprio.contains(outro) &&
            outro.closest('[hidden], [aria-hidden="true"], [inert]') === null &&
            (proprio.compareDocumentPosition(outro) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0,
        )
      ) {
        return
      }
      evento.preventDefault()
      fechar()
    }
    document.addEventListener('keydown', aoTeclar, true)
    return () => {
      document.removeEventListener('keydown', aoTeclar, true)
    }
  }, [fechar])

  return (
    <div className="ficha__fundo-do-dialogo" onClick={fechar} role="presentation">
      <div
        ref={caixa}
        className="ficha__dialogo"
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
        onClick={(evento) => {
          evento.stopPropagation()
        }}
      >
        <div className="ficha__dialogo-topo">
          <span className="ficha__dialogo-titulo">{titulo}</span>
          <span className="ficha__dialogo-sub">{subtitulo}</span>
        </div>
        <div className="ficha__dialogo-corpo">
          {children}
          {erro === undefined ? null : (
            <p className="ficha__dialogo-erro" role="alert">
              {mensagemDe(erro)}
            </p>
          )}
        </div>
        <div className="ficha__dialogo-rodape">
          {/* O botão da esquerda: "Cancelar" por padrão, e a ação secundária quando ela existe.
              Os dois ocupam o **mesmo** lugar de propósito — quem navega por teclado encontra o
              botão que sai (ou o que decide sem sugerir) sempre na mesma posição. */}
          {acaoSecundaria === undefined ? (
            <button
              type="button"
              className="ficha__dialogo-botao"
              disabled={ocupado}
              onClick={fechar}
            >
              Cancelar
            </button>
          ) : (
            <button
              type="button"
              className="ficha__dialogo-botao"
              disabled={ocupado}
              onClick={acaoSecundaria.aoAcionar}
            >
              {acaoSecundaria.rotulo}
            </button>
          )}
          {semConfirmar ? null : (
            <button
              type="button"
              className="ficha__dialogo-botao ficha__dialogo-botao--primario"
              disabled={confirmarDesabilitado || ocupado}
              onClick={aoConfirmar}
            >
              {rotuloDoConfirmar}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
